/**
 * Creates a Modal instance.
 * @param {Object} options - Options for configuring the modal.
 * @param {string} [options.backgroundColor] - Background color for the modal container.
 * @param {string} [options.padding] - Padding for the modal container.
 * @param {string} [options.borderRadius] - Border radius for the modal container.
 * @param {string} [options.boxShadow] - Box shadow for the modal container.
 * @param {string} [options.width] - Width of the modal container.
 * @param {string} [options.maxWidth] - Maximum width of the modal container.
 * @param {string} [options.height] - Height of the modal container.
 * @param {string} [options.className] - CSS class for the modal container.
 * @param {string} [options.containerId='default-modal'] - ID for the modal container.
 * @param {boolean} [options.outsideClickCloses=true] - Whether clicking outside the modal closes it.
 */
export class Modal {
    static openModals = []; // Static array to track open modals
    onClose = null;
    
    constructor({
        backgroundColor,
        padding,
        borderRadius,
        boxShadow,
        width,
        maxWidth,
        height,
        className,
        containerId = 'default-modal',
        outsideClickCloses = true,
    } = {}) {
        this.onClose
        // Create modal elements
        this.modalOverlay = document.createElement('div');
        this.modalContainer = document.createElement('div');
        this.closeButton = document.createElement('button');
        this.closeButton.className = 'btn-close';

        // Create and append the image element for the close button
        const closeIcon = document.createElement('img');
        closeIcon.src = `${__webpack_public_path__}/images/close.png`;
        closeIcon.alt = "Close";
        closeIcon.dataset.defaultSrc = closeIcon.src;
        closeIcon.dataset.hoverSrc = `${__webpack_public_path__}/images/close-hover.png`;
        this.closeButton.appendChild(closeIcon);

        // Add hover effects for the close button
        this.closeButton.addEventListener('mouseenter', () => {
            closeIcon.src = closeIcon.dataset.hoverSrc;
        });
        this.closeButton.addEventListener('mouseleave', () => {
            closeIcon.src = closeIcon.dataset.defaultSrc;
        });

        // Set unique ID for the container
        this.modalContainer.id = containerId;

        // Set up styles for the overlay
        this.modalOverlay.style.position = 'fixed';
        this.modalOverlay.style.top = '0';
        this.modalOverlay.style.left = '0';
        this.modalOverlay.style.width = '100%';
        this.modalOverlay.style.height = '100%';
        this.modalOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
        this.modalOverlay.style.display = 'flex';
        this.modalOverlay.style.alignItems = 'center';
        this.modalOverlay.style.justifyContent = 'center';
        this.modalOverlay.style.zIndex = '1000';
        this.modalOverlay.style.visibility = 'hidden';

        // Set up styles for the modal container
        this.modalContainer.style.position = 'relative';
        if (backgroundColor) this.modalContainer.style.backgroundColor = backgroundColor;
        if (height) this.modalContainer.style.height = height;
        if (padding !== undefined) this.modalContainer.style.padding = padding;
        if (borderRadius !== undefined) this.modalContainer.style.borderRadius = borderRadius;
        if (boxShadow) this.modalContainer.style.boxShadow = boxShadow;
        if (width !== undefined) this.modalContainer.style.width = width;
        if (maxWidth !== undefined) this.modalContainer.style.maxWidth = maxWidth;
        if (className !== undefined) {
            this.modalContainer.className = className;
        } else {
            this.modalContainer.className = 'os-modal';
        }

        // Set up the close button styles
        this.closeButton.style.position = 'absolute';
        this.closeButton.style.top = '10px';
        this.closeButton.style.right = '10px';
        this.closeButton.style.cursor = 'pointer';

        // Append the close button to the modal container
        this.modalContainer.appendChild(this.closeButton);

        // Append the modal container to the overlay
        this.modalOverlay.appendChild(this.modalContainer);

        // Close on outside click if enabled
        if (outsideClickCloses) {
            this.modalOverlay.addEventListener('mousedown', (event) => {
                if (event.target === this.modalOverlay) {
                    this.close();
                }
            });
        }

        // Event listener for the close button
        this.closeButton.addEventListener('click', () => this.close());

        // Initialize focus management properties
        this.isOpen = false;
        this.previousFocus = null;
        this.firstFocusable = null;
        this.lastFocusable = null;

        // Keydown handler for focus trapping and Escape key
        this.handleKeyDown = (this.handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                // Only close if this is the topmost modal
                if (Modal.openModals[Modal.openModals.length - 1] === this) {
                    this.close();
                }
            } else if (event.key === 'Tab') {
                // Only trap focus if this is the topmost modal
                if (Modal.openModals[Modal.openModals.length - 1] === this) {
                    const activeElement = document.activeElement;
                    if (!this.modalContainer.contains(activeElement)) {
                        event.preventDefault();
                        this.firstFocusable.focus();
                    } else {
                        if (!event.shiftKey && activeElement === this.lastFocusable) {
                            event.preventDefault();
                            this.firstFocusable.focus();
                        } else if (event.shiftKey && activeElement === this.firstFocusable) {
                            event.preventDefault();
                            this.lastFocusable.focus();
                        }
                    }
                }
            }
        });
    }

    /**
     * Updates the list of focusable elements within the modal.
     */
    updateFocusableElements() {
        const focusableElementsString = 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';
        const focusableElements = Array.from(this.modalContainer.querySelectorAll(focusableElementsString));
        if (focusableElements.length > 0) {
            this.firstFocusable = focusableElements[0];
            this.lastFocusable = focusableElements[focusableElements.length - 1];
        } else {
            // Fallback: make the container focusable if no focusable elements exist
            this.modalContainer.tabIndex = 0;
            this.firstFocusable = this.modalContainer;
            this.lastFocusable = this.modalContainer;
        }
    }

    /**
     * Opens the modal, sets focus, and traps it within the modal.
     * @returns {Modal} The current modal instance.
     */
    open() {
        this.previousFocus = document.activeElement;
        document.body.appendChild(this.modalOverlay);
        this.modalOverlay.style.visibility = 'visible';
        this.isOpen = true;
        Modal.openModals.push(this); // Add to stack of open modals
        this.updateFocusableElements();
        this.firstFocusable.focus();
        document.addEventListener('keydown', this.handleKeyDown);
        return this;
    }

    /**
     * Updates the modal content and recalculates focusable elements.
     * @param {string|Node} newContent - The new content to display in the modal.
     * @returns {Modal} The current modal instance.
     */
    update(newContent) {
        // Remove all children except the close button
        Array.from(this.modalContainer.children).forEach((child) => {
            if (child !== this.closeButton) {
                child.remove();
            }
        });

        // Add new content
        if (typeof newContent === 'string') {
            this.modalContainer.insertAdjacentHTML('beforeend', newContent);
        } else if (newContent instanceof Node) {
            this.modalContainer.appendChild(newContent);
        }

        this.updateFocusableElements();
        return this;
    }

    /**
     * Closes the modal, removes the event listener, and restores focus.
     */
    close() {
        if (typeof this.onClose === 'function') {
            this.onClose();
        }
        this.modalOverlay.remove();
        this.isOpen = false;
        Modal.openModals = Modal.openModals.filter(modal => modal !== this); // Remove from stack
        document.removeEventListener('keydown', this.handleKeyDown);
        if (this.previousFocus && document.body.contains(this.previousFocus)) {
            this.previousFocus.focus();
        }
    }
}